import { Link } from 'react-router-dom';
import { MapPin, Eye, Star } from 'lucide-react';
import { DealTimerBadge } from './DealTimer';

const CATEGORY_COLORS = {
  'Food & Drink': '#C8A97E',
  'Nightlife': '#8A9A8B',
  'Fitness & Wellness': '#7BA05B',
  'Activities': '#A07BC8',
  'Free Deals': '#C87B7B',
  'Convenience Stores': '#5B9EA0',
  'Liquor Stores': '#8B4A62',
};

export default function DealCard({ deal }) {
  const categoryColor = CATEGORY_COLORS[deal.category] || '#C8A97E';

  return (
    <Link
      to={`/deals/${deal.deal_id}`}
      data-testid={`deal-card-${deal.deal_id}`}
      data-testid="deal-card"
      className="deal-card block bg-white rounded-2xl overflow-hidden border border-[#8A9A8B]/10 shadow-sm hover:shadow-lg transition-all duration-300 group"
    >
      {/* Image */}
      <div className="aspect-[4/3] overflow-hidden relative">
        <img
          src={deal.image_url}
          alt={deal.title}
          data-testid="deal-card-image"
          className="deal-card-image w-full h-full object-cover"
          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&q=80'; }}
        />
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          <span
            className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full text-white"
            style={{ backgroundColor: categoryColor }}
          >
            {deal.category}
          </span>
          {deal.is_featured && (
            <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#2B2B2B] text-[#F6F1E9] flex items-center gap-1">
              <Star size={10} fill="currentColor" /> Featured
            </span>
          )}
          {deal.age_restriction === '21+' && (
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#8B1A1A] text-white" data-testid="age-restriction-badge">
              21+
            </span>
          )}
        </div>
        {/* Discount badge */}
        {deal.discount_text && (
          <div className="absolute top-3 right-3 bg-white rounded-full px-2.5 py-1">
            <span className="text-xs font-bold text-[#C8A97E]">{deal.discount_text}</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <p className="text-xs font-semibold text-[#8A9A8B] uppercase tracking-wider mb-1" data-testid="deal-card-business">
          {deal.business_name}
        </p>
        <h3
          className="text-base font-bold text-[#2B2B2B] leading-snug mb-2 line-clamp-2"
          data-testid="deal-card-title"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          {deal.title}
        </h3>
        <p className="text-xs text-[#2B2B2B]/55 line-clamp-2 mb-3 leading-relaxed">
          {deal.description}
        </p>

        {/* Price + Location row */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {deal.price !== null && deal.price !== undefined ? (
              <span className="text-base font-bold text-[#2B2B2B]">
                {deal.price === 0 ? 'FREE' : `$${deal.price}`}
              </span>
            ) : null}
            {deal.original_price && deal.original_price > 0 && (
              <span className="text-xs text-[#2B2B2B]/40 line-through">${deal.original_price}</span>
            )}
          </div>
          <div className="flex items-center gap-1 text-[#8A9A8B]" data-testid="deal-card-location">
            <MapPin size={11} />
            <span className="text-xs truncate max-w-[110px]">
              {deal.business_address
                ? deal.business_address.split(',')[0].trim()
                : deal.city_name}
            </span>
          </div>
        </div>

        {/* Timer badge + views */}
        <div className="flex items-center justify-between">
          <DealTimerBadge endDate={deal.end_date} />
          <div className="flex items-center gap-1 text-[#2B2B2B]/30">
            <Eye size={11} />
            <span className="text-xs">{deal.views}</span>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="px-4 pb-4">
        <div className="w-full py-2 rounded-full bg-[#F6F1E9] text-center text-xs font-bold text-[#2B2B2B] group-hover:bg-[#2B2B2B] group-hover:text-[#F6F1E9] transition-colors duration-300" data-testid="deal-card-cta">
          View Deal
        </div>
      </div>
    </Link>
  );
}
